import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { getAccessToken } from "../../utils/spotifyToken";
import { Search } from "lucide-react";
import axiosPublic from "../../api/axiosConfig";

function TrackSearch({ userId, dj, refreshUserRequests }) {
  const [query, setQuery] = useState("");
  const [tracks, setTracks] = useState([]);

  const [selectedTrack, setSelectedTrack] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [username, setUsername] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const searchTrack = async () => {
    setLoading(true);

    try {
      const token = await getAccessToken();
      const response = await axios.get("https://api.spotify.com/v1/search", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          q: query,
          type: "track",
        },
      });

      setTracks(response.data.tracks.items);
    } catch (err) {
      toast.error("Error searching for tracks. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTrack = (track) => {
    setSelectedTrack(track);
    setIsModalOpen(true);
  };

  const [errors, setErrors] = useState({ name: "", message: "" });
  const [isFormValid, setIsFormValid] = useState(false);

  const validateInput = () => {
    let nameError = "";
    let messageError = "";

    const trimmedName = username.trim();
    const trimmedMessage = message.trim();

    if (trimmedName.length < 3) {
      nameError = "Name must be at least 3 characters.";
    } else if (!/^[A-Za-zА-Яа-яЁё\s'-]+$/.test(trimmedName)) {
      nameError =
        "Name can only contain letters, spaces, hyphens, or apostrophes.";
    }

    if (trimmedMessage.length > 100) {
      messageError = "Message cannot exceed 100 characters.";
    } else if (/<\/?[^>]+(>|$)/g.test(trimmedMessage)) {
      messageError = "HTML tags are not allowed in the message.";
    }

    setErrors({ name: nameError, message: messageError });
    setIsFormValid(!nameError && !messageError);
  };

  useEffect(() => {
    validateInput();
  }, [username, message]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!dj) {
      toast.warning("Please, select location or DJ");
      return;
    }

    if (!username) {
      toast("Please enter your name");
      return;
    }

    if (isFormValid) {
      const requestData = {
        trackName: selectedTrack.name,
        artist: selectedTrack.artists[0].name,
        albumArt: selectedTrack.album.images[0]?.url,
        username: username.trim(),
        message: message.trim(),
        userId: userId,
        djId: dj.id,
      };

      try {
        await axiosPublic.post("/request/send", requestData, {
          headers: {
            "Content-Type": "application/json",
          },
        });
        toast.success("Request successfully sent");
        setIsModalOpen(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
        setUsername("");
        setMessage("");
        refreshUserRequests();
        setTracks([]);
        setQuery("");
      } catch (error) {
        toast.warning("Something went wrong...");
      }
    }
  };

  if (!userId) {
    console.error("userId is required bot not provided");
    return null;
  }

  return (
    <div className="min-h-[300px] text-white flex flex-col items-center px-4 py-6">
      <h1 className="text-3xl text-white font-bold mb-6">Search</h1>
      <div className="flex gap-4 mb-6 w-full max-w-lg">
        <div className="relative flex-grow">
          <input
            type="text"
            placeholder="Enter track name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full p-3 rounded-lg bg-secondary text-textMain placeholder-textMuted focus:outline-none focus:ring-2 focus:ring-accent pl-10"
          />
          <Search className="absolute left-3 top-3 text-textMuted w-5 h-5" />
        </div>
        <button
          onClick={searchTrack}
          disabled={loading || query.trim() === ""}
          className={`p-3 rounded-lg text-primary font-bold transition ${
            loading
              ? "bg-secondary cursor-not-allowed"
              : "bg-accent hover:bg-opacity-80"
          }`}
        >
          Search
        </button>
      </div>

      <div className="flex flex-col gap-4 w-full max-w-lg">
        {tracks.map((track) => (
          <div
            key={track.id}
            className="outline outline-2 outline-secondary rounded-xl p-4 flex items-center justify-between shadow-md"
          >
            <div className="flex items-center gap-4">
              <img
                src={track.album.images[0]?.url || "https://placehold.co/50"}
                alt={track.name}
                className="w-16 h-16 rounded-lg"
              />
              <div>
                <p className="font-bold">{track.name}</p>
                <p className="text-sm text-gray-400">
                  {track.artists[0]?.name}
                </p>
              </div>
            </div>
            <button
              onClick={() => handleSelectTrack(track)}
              className="outline outline-2 outline-secondary hover:bg-secondary text-white px-4 py-2 rounded-lg font-bold"
            >
              Select
            </button>
          </div>
        ))}
      </div>

      {/* Modal window */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-notificationList p-6 rounded-xl max-w-sm w-full shadow-lg">
            <h2 className="text-xl font-bold mb-4">Submit Request</h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1 text-textMuted">
                  Your Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className={`w-full p-3 rounded-lg shadow-md bg-black/30 outline outline-1 text-textMuted ${
                    errors.name && username
                      ? "outline-red-500"
                      : "outline-notificationList"
                  }`}
                  placeholder="Enter your name"
                />
                {errors.name && username && (
                  <p className="text-red-500 text-xs mt-1">{errors.name}</p>
                )}
              </div>

              <div className="mb-2">
                <label className="block text-sm font-medium mb-1 text-textMuted">
                  Message for DJ (optional)
                </label>
                <textarea
                  name="message"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className={`w-full p-3 rounded-lg shadow-md bg-black/30 outline outline-1 text-textMuted ${
                    errors.message
                      ? "outline-red-500"
                      : "outline-notificationList"
                  }`}
                  placeholder="Enter a message"
                ></textarea>
                {errors.message && (
                  <p className="text-red-500 text-xs mt-1">{errors.message}</p>
                )}
              </div>
              {dj && <p className="">Price: {dj.currentPrice} PLN</p>}
              <div className="flex justify-end gap-2 mt-4">
                <button
                  type="button"
                  className="outline outline-1 outline-secondary px-4 py-2 mr-2 rounded-lg text-white hover:bg-secondary"
                  onClick={() => {
                    setIsModalOpen(false);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedTrack || !isFormValid}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    selectedTrack && isFormValid
                      ? "bg-accent hover:bg-opacity-80 text-black"
                      : "outline outline-1 outline-secondary cursor-not-allowed"
                  }`}
                >
                  Submit
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrackSearch;
